import { CalendarDate } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { CalendarDateInput } from "components/ui/calendar-date";
import { defaultCardStyle } from "components/ui/card";
import { SortDown, SortUp } from "components/ui/icon";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useRegionId } from "store/selectors/hooks";
import { referralSourceReportSlice } from "store/slices/referral-source-report";

type RouterOutput = inferRouterOutputs<AppRouter>;
type ReportResult = RouterOutput["reportsV3"]["referralSourceReport"];
type ContactRow = ReportResult["contacts"][number];
type SortField =
    | "contactName"
    | "leadCount"
    | "movedCount"
    | "moveRate"
    | "avgDaysToMove"
    | "totalInvoicedCents"
    | "avgInvoicePerMoveCents";

type ViewMode = "flat" | "category";

interface DatePreset {
    label: string;
    getRange: () => { start: CalendarDate; end: CalendarDate };
}

const getDatePresets = (): DatePreset[] => {
    const today = CalendarDate.today;
    return [
        {
            label: "Year to Date",
            getRange: () => ({
                start: new CalendarDate(today.year, 1, 1),
                end: today,
            }),
        },
        {
            label: "This Month",
            getRange: () => ({
                start: new CalendarDate(today.year, today.month, 1),
                end: today,
            }),
        },
        {
            label: "Last Month",
            getRange: () => {
                const lastMonth =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 12, 1)
                        : new CalendarDate(today.year, today.month - 1, 1);
                const endDay = new Date(lastMonth.year, lastMonth.month, 0).getDate();
                return {
                    start: lastMonth,
                    end: new CalendarDate(lastMonth.year, lastMonth.month, endDay),
                };
            },
        },
        {
            label: "This Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                return {
                    start: new CalendarDate(today.year, q * 3 + 1, 1),
                    end: today,
                };
            },
        },
        {
            label: "Last Quarter",
            getRange: () => {
                const q = Math.floor((today.month - 1) / 3);
                const prevQ = q === 0 ? 3 : q - 1;
                const year = q === 0 ? today.year - 1 : today.year;
                const startMonth = prevQ * 3 + 1;
                const endMonth = startMonth + 2;
                const endDay = new Date(year, endMonth, 0).getDate();
                return {
                    start: new CalendarDate(year, startMonth, 1),
                    end: new CalendarDate(year, endMonth, endDay),
                };
            },
        },
        {
            label: "Last 12 Months",
            getRange: () => {
                const start =
                    today.month === 1
                        ? new CalendarDate(today.year - 1, 1, today.day)
                        : new CalendarDate(today.year - 1, today.month, today.day);
                return { start, end: today };
            },
        },
        {
            label: "Last Year",
            getRange: () => ({
                start: new CalendarDate(today.year - 1, 1, 1),
                end: new CalendarDate(today.year - 1, 12, 31),
            }),
        },
    ];
};

const PAGE_SIZE = 25;

const formatCurrency = (cents: number): string => {
    return (cents / 100).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
};

const formatPercent = (rate: number | null): string => {
    if (rate === null) return "-";
    return `${Math.round(rate * 100)}%`;
};

const ProgressBar: React.FC<{
    value: number;
    max: number;
    className: string;
}> = ({ value, max, className }) => {
    const width = max > 0 ? (value / max) * 100 : 0;
    return (
        <div className="w-full rounded-full mt-1 h-1 bg-gray-200">
            <div
                className={`rounded-full h-1 ${className}`}
                style={{ width: `${Math.min(width, 100)}%` }}
            />
        </div>
    );
};

export const ReferralSource: React.FC = () => {
    const regionId = useRegionId();
    const dispatch = useAppDispatch();
    const reportState = useAppSelector(state => state.referralSourceReport);

    const startDate = reportState.startDate;
    const endDate = reportState.endDate;
    const sortField = reportState.sortField as SortField;
    const sortAsc = reportState.sortAsc;
    const viewMode = reportState.viewMode as ViewMode;
    const categoryFilter = reportState.categoryFilter;
    const assignedUserFilter = reportState.assignedUserFilter;

    const updateState = (partial: Partial<typeof reportState>) => {
        dispatch(referralSourceReportSlice.actions.update(partial));
    };

    const presets = useMemo(() => getDatePresets(), []);

    const [page, setPage] = useState(0);

    const trpcClient = trpc.useTRPC();

    const reportQuery = useQuery(
        trpcClient.reportsV3.referralSourceReport.queryOptions(
            {
                startDate,
                endDate,
                regionId: regionId || "",
            },
            { enabled: !!regionId },
        ),
    );

    const filteredContacts = useMemo(() => {
        if (!reportQuery.data) return [];
        let contacts = reportQuery.data.contacts;
        if (categoryFilter !== "all") {
            if (categoryFilter === "uncategorized") {
                contacts = contacts.filter(c => !c.organizationType);
            } else {
                contacts = contacts.filter(c => c.organizationType === categoryFilter);
            }
        }
        if (assignedUserFilter !== "all") {
            contacts = contacts.filter(c => c.assignedUserId === assignedUserFilter);
        }
        return contacts;
    }, [reportQuery.data, categoryFilter, assignedUserFilter]);

    const sortedContacts = useMemo(() => {
        const sorted = [...filteredContacts].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];
            if (aVal === null && bVal === null) return 0;
            if (aVal === null) return 1;
            if (bVal === null) return -1;
            if (typeof aVal === "string" && typeof bVal === "string") {
                return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortAsc
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
        return sorted;
    }, [filteredContacts, sortField, sortAsc]);

    const paginatedContacts = useMemo(() => {
        const end = (page + 1) * PAGE_SIZE;
        return sortedContacts.slice(0, end);
    }, [sortedContacts, page]);

    const maxValues = useMemo(() => {
        if (filteredContacts.length === 0)
            return {
                leads: 1,
                moved: 1,
                active: 1,
                closedNoMove: 1,
                invoiced: 1,
                avgMove: 1,
            };
        return {
            leads: Math.max(...filteredContacts.map(c => c.leadCount), 1),
            moved: Math.max(...filteredContacts.map(c => c.movedCount), 1),
            active: Math.max(...filteredContacts.map(c => c.activeCount), 1),
            closedNoMove: Math.max(...filteredContacts.map(c => c.closedNoMoveCount), 1),
            invoiced: Math.max(...filteredContacts.map(c => c.totalInvoicedCents), 1),
            avgMove: Math.max(
                ...filteredContacts.map(c => c.avgInvoicePerMoveCents || 0),
                1,
            ),
        };
    }, [filteredContacts]);

    const summary = useMemo(() => {
        const totalLeads = filteredContacts.reduce((s, c) => s + c.leadCount, 0);
        const totalMoved = filteredContacts.reduce((s, c) => s + c.movedCount, 0);
        const totalClosedNoMove = filteredContacts.reduce(
            (s, c) => s + c.closedNoMoveCount,
            0,
        );
        const totalActive = filteredContacts.reduce((s, c) => s + c.activeCount, 0);
        const totalInvoiced = filteredContacts.reduce(
            (s, c) => s + c.totalInvoicedCents,
            0,
        );
        const moveRate = totalLeads > 0 ? totalMoved / totalLeads : null;

        const movedWithDays = filteredContacts.filter(
            c => c.avgDaysToMove !== null && c.movedCount > 0,
        );
        let avgDays: number | null = null;
        if (movedWithDays.length > 0) {
            const totalDays = movedWithDays.reduce(
                (s, c) => s + (c.avgDaysToMove || 0) * c.movedCount,
                0,
            );
            const totalMovedWithDays = movedWithDays.reduce(
                (s, c) => s + c.movedCount,
                0,
            );
            avgDays =
                totalMovedWithDays > 0
                    ? Math.round(totalDays / totalMovedWithDays)
                    : null;
        }

        return {
            totalContacts: filteredContacts.length,
            totalLeads,
            totalMoved,
            totalClosedNoMove,
            totalActive,
            moveRate,
            avgDays,
            totalInvoiced,
        };
    }, [filteredContacts]);

    const categories = useMemo(() => {
        if (!reportQuery.data) return [];
        const types = new Set<string>();
        let hasUncategorized = false;
        for (const c of reportQuery.data.contacts) {
            if (c.organizationType) {
                types.add(c.organizationType);
            } else {
                hasUncategorized = true;
            }
        }
        const sorted = Array.from(types).sort();
        if (hasUncategorized) sorted.push("uncategorized");
        return sorted;
    }, [reportQuery.data]);

    const assignedUsers = useMemo(() => {
        if (!reportQuery.data) return [];
        const users = new Map<string, string>();
        for (const c of reportQuery.data.contacts) {
            if (c.assignedUserId && c.assignedUserName) {
                users.set(c.assignedUserId, c.assignedUserName);
            }
        }
        return Array.from(users.entries()).sort((a, b) => a[1].localeCompare(b[1]));
    }, [reportQuery.data]);

    const groupedByCategory = useMemo(() => {
        if (viewMode !== "category") return null;
        const groups = new Map<string, ContactRow[]>();
        for (const c of sortedContacts) {
            const key = c.organizationType || "Uncategorized";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(c);
        }
        const entries = Array.from(groups.entries()).sort((a, b) => {
            if (a[0] === "Uncategorized") return 1;
            if (b[0] === "Uncategorized") return -1;
            return a[0].localeCompare(b[0]);
        });
        return entries;
    }, [sortedContacts, viewMode]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            updateState({ sortAsc: !sortAsc });
        } else {
            updateState({ sortField: field, sortAsc: false });
        }
        setPage(0);
    };

    const handlePreset = (preset: DatePreset) => {
        const range = preset.getRange();
        updateState({
            startDate: range.start.toString(),
            endDate: range.end.toString(),
        });
        setPage(0);
    };

    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortAsc ? (
            <SortUp className="inline ml-1" />
        ) : (
            <SortDown className="inline ml-1" />
        );
    };

    const renderTableRow = (contact: ContactRow) => (
        <tr key={contact.contactId} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="py-3 px-4">
                <Link
                    to={`/references/contacts/show/${contact.contactId}/inbound`}
                    className="font-medium text-blue-700 hover:underline">
                    {contact.preferredName || contact.contactName}
                </Link>
                {contact.companyName && (
                    <div className="text-xs text-gray-500">{contact.companyName}</div>
                )}
                {contact.organizationType && (
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                        {contact.organizationType}
                    </span>
                )}
            </td>
            <td className="py-3 px-4 text-sm text-gray-700">
                {contact.assignedUserName || <span className="text-gray-400">-</span>}
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.leadCount}</div>
                <ProgressBar
                    value={contact.leadCount}
                    max={maxValues.leads}
                    className="bg-blue-600"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.movedCount}</div>
                <ProgressBar
                    value={contact.movedCount}
                    max={maxValues.moved}
                    className="bg-green-600"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.activeCount}</div>
                <ProgressBar
                    value={contact.activeCount}
                    max={maxValues.active}
                    className="bg-gray-400"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">{contact.closedNoMoveCount}</div>
                <ProgressBar
                    value={contact.closedNoMoveCount}
                    max={maxValues.closedNoMove}
                    className="bg-amber-500"
                />
            </td>
            <td className="py-3 px-4 text-right text-sm">
                {contact.avgDaysToMove !== null ? (
                    <span>
                        {contact.avgDaysToMove}
                        {contact.hasMissingMoveDate && (
                            <span
                                className="text-amber-500 ml-1"
                                title="Some moved clients are missing move dates">
                                *
                            </span>
                        )}
                    </span>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {contact.totalInvoicedCents > 0
                        ? formatCurrency(contact.totalInvoicedCents)
                        : "-"}
                </div>
                <ProgressBar
                    value={contact.totalInvoicedCents}
                    max={maxValues.invoiced}
                    className="bg-purple-600"
                />
            </td>
            <td className="py-3 px-4 text-right">
                <div className="font-medium">
                    {contact.avgInvoicePerMoveCents !== null
                        ? formatCurrency(contact.avgInvoicePerMoveCents)
                        : "-"}
                </div>
                {contact.avgInvoicePerMoveCents !== null && (
                    <ProgressBar
                        value={contact.avgInvoicePerMoveCents}
                        max={maxValues.avgMove}
                        className="bg-orange-600"
                    />
                )}
            </td>
        </tr>
    );

    const renderFooterTotals = () => {
        return (
            <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                <td className="py-3 px-4">Total ({filteredContacts.length} contacts)</td>
                <td className="py-3 px-4" />
                <td className="py-3 px-4 text-right">{summary.totalLeads}</td>
                <td className="py-3 px-4 text-right">{summary.totalMoved}</td>
                <td className="py-3 px-4 text-right">{summary.totalActive}</td>
                <td className="py-3 px-4 text-right">{summary.totalClosedNoMove}</td>
                <td className="py-3 px-4 text-right">
                    {summary.avgDays !== null ? summary.avgDays : "-"}
                </td>
                <td className="py-3 px-4 text-right">
                    {summary.totalInvoiced > 0
                        ? formatCurrency(summary.totalInvoiced)
                        : "-"}
                </td>
                <td className="py-3 px-4" />
            </tr>
        );
    };

    const startDateValue = CalendarDate.parse(startDate).getOrElse(CalendarDate.today);
    const endDateValue = CalendarDate.parse(endDate).getOrElse(CalendarDate.today);

    return (
        <Stage>
            <PageHeader title="Referral Source Report" />
            <ContentContainer>
                <div className={`${defaultCardStyle} p-4 mb-6`}>
                    <div className="flex flex-wrap items-end gap-3">
                        <div className="w-40">
                            <CalendarDateInput
                                label="Start Date"
                                value={startDateValue}
                                onChange={v => {
                                    if (v) {
                                        updateState({
                                            startDate: v.toString(),
                                        });
                                    }
                                    setPage(0);
                                }}
                            />
                        </div>
                        <div className="w-40">
                            <CalendarDateInput
                                label="End Date"
                                value={endDateValue}
                                onChange={v => {
                                    if (v) {
                                        updateState({
                                            endDate: v.toString(),
                                        });
                                    }
                                    setPage(0);
                                }}
                            />
                        </div>
                        <div className="flex flex-wrap gap-1 ml-2">
                            {presets.map(p => (
                                <button
                                    key={p.label}
                                    onClick={() => handlePreset(p)}
                                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100">
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {reportQuery.isLoading && (
                    <div className="text-center py-12 text-gray-500">
                        Loading report...
                    </div>
                )}

                {reportQuery.isError && (
                    <div className="text-center py-12 text-red-600">
                        Failed to load report. Please try again.
                    </div>
                )}

                {reportQuery.data && (
                    <>
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <div>
                                <select
                                    value={categoryFilter}
                                    onChange={e => {
                                        updateState({
                                            categoryFilter: e.target.value,
                                        });
                                        setPage(0);
                                    }}
                                    className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500">
                                    <option value="all">All Categories</option>
                                    {categories.map(cat => (
                                        <option key={cat} value={cat}>
                                            {cat === "uncategorized"
                                                ? "Uncategorized"
                                                : cat}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <select
                                    value={assignedUserFilter}
                                    onChange={e => {
                                        updateState({
                                            assignedUserFilter: e.target.value,
                                        });
                                        setPage(0);
                                    }}
                                    className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500">
                                    <option value="all">All Users</option>
                                    {assignedUsers.map(([id, name]) => (
                                        <option key={id} value={id}>
                                            {name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex rounded-md border border-gray-300 overflow-hidden">
                                <button
                                    onClick={() => updateState({ viewMode: "flat" })}
                                    className={`px-3 py-1 text-xs font-medium ${
                                        viewMode === "flat"
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}>
                                    Flat
                                </button>
                                <button
                                    onClick={() => updateState({ viewMode: "category" })}
                                    className={`px-3 py-1 text-xs font-medium border-l border-gray-300 ${
                                        viewMode === "category"
                                            ? "bg-blue-600 text-white"
                                            : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}>
                                    By Category
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Contacts
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalContacts}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-blue-700 uppercase font-medium">
                                    Total Clients
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalLeads}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-green-700 uppercase font-medium">
                                    Total Moved
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.totalMoved}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {formatPercent(summary.moveRate)} move rate
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Avg Days to Move
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {summary.avgDays !== null ? summary.avgDays : "-"}
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-purple-700 uppercase font-medium">
                                    Total Invoiced
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(summary.totalInvoiced)}
                                </div>
                            </div>
                        </div>

                        <div className="text-sm text-gray-500 mb-2">
                            Showing{" "}
                            {Math.min(paginatedContacts.length, sortedContacts.length)} of{" "}
                            {sortedContacts.length} contacts
                            {filteredContacts.length !==
                                (reportQuery.data?.contacts.length ?? 0) &&
                                ` (filtered from ${reportQuery.data?.contacts.length})`}
                        </div>

                        <div className={`${defaultCardStyle} overflow-x-auto`}>
                            <table className="min-w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th
                                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("contactName")}>
                                            Contact
                                            {sortIndicator("contactName")}
                                        </th>
                                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">
                                            Assigned To
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-blue-700 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("leadCount")}>
                                            Clients
                                            {sortIndicator("leadCount")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-green-700 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("movedCount")}>
                                            Moved
                                            {sortIndicator("movedCount")}
                                        </th>
                                        <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase">
                                            Active
                                        </th>
                                        <th className="py-3 px-4 text-right text-xs font-semibold text-amber-700 uppercase">
                                            Closed
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort("avgDaysToMove")}>
                                            Avg Days to Move
                                            {sortIndicator("avgDaysToMove")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-purple-700 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() =>
                                                handleSort("totalInvoicedCents")
                                            }>
                                            Total Invoiced
                                            {sortIndicator("totalInvoicedCents")}
                                        </th>
                                        <th
                                            className="py-3 px-4 text-right text-xs font-semibold text-orange-700 uppercase cursor-pointer hover:bg-gray-100"
                                            onClick={() =>
                                                handleSort("avgInvoicePerMoveCents")
                                            }>
                                            Avg/Move
                                            {sortIndicator("avgInvoicePerMoveCents")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewMode === "flat" ? (
                                        paginatedContacts.map(renderTableRow)
                                    ) : (
                                        <>
                                            {groupedByCategory?.map(
                                                ([category, contacts]) => {
                                                    const groupLeads = contacts.reduce(
                                                        (s, c) => s + c.leadCount,
                                                        0,
                                                    );
                                                    const groupMoved = contacts.reduce(
                                                        (s, c) => s + c.movedCount,
                                                        0,
                                                    );
                                                    const groupInvoiced = contacts.reduce(
                                                        (s, c) =>
                                                            s + c.totalInvoicedCents,
                                                        0,
                                                    );
                                                    const groupRate =
                                                        groupLeads > 0
                                                            ? Math.round(
                                                                  (groupMoved /
                                                                      groupLeads) *
                                                                      100,
                                                              )
                                                            : 0;
                                                    return (
                                                        <React.Fragment key={category}>
                                                            <tr className="bg-gray-100 border-b border-gray-200">
                                                                <td
                                                                    colSpan={9}
                                                                    className="py-2 px-4 text-sm font-semibold text-gray-700">
                                                                    <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2" />
                                                                    {category}{" "}
                                                                    <span className="font-normal text-gray-500">
                                                                        {contacts.length}{" "}
                                                                        contacts -{" "}
                                                                        {groupLeads}{" "}
                                                                        clients -{" "}
                                                                        {groupMoved} moved
                                                                        ({groupRate}
                                                                        %) -{" "}
                                                                        {formatCurrency(
                                                                            groupInvoiced,
                                                                        )}{" "}
                                                                        invoiced
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                            {contacts.map(renderTableRow)}
                                                        </React.Fragment>
                                                    );
                                                },
                                            )}
                                        </>
                                    )}
                                    {filteredContacts.length > 0 && renderFooterTotals()}
                                </tbody>
                            </table>
                        </div>

                        {sortedContacts.length > (page + 1) * PAGE_SIZE && (
                            <div className="text-center mt-4">
                                <button
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                                    Show More
                                </button>
                            </div>
                        )}

                        <div className="mt-4 mb-8 text-xs text-gray-400 space-y-1">
                            <p>
                                * Moved statuses include: Moved, Invoice Sent, Closed -
                                Paid.
                            </p>
                            <p>
                                * Avg Days to Move is calculated from client created date
                                to move date. Clients without a move date are excluded.
                            </p>
                            <p>
                                * Only contacts with at least one client in the selected
                                date range are shown.
                            </p>
                        </div>
                    </>
                )}
            </ContentContainer>
        </Stage>
    );
};
